import { todayStr } from "../domain/date";
import type { DayLog, DayTests, EatingProfile, KayaState, Profile, TrainingProfile, WaterSettings } from "../domain/types";
import { mergeState, type Stamped } from "./merge";

/*
 * What a linked account may write into another person's state, and how it lands (ADR-046; the
 * exercise answers joined in ADR-050).
 *
 * Since ADR-056 the exercise answers carry the plan the owner sets, and one day's test counts the
 * owner timed beside the parent can come with them; the safety answers and starting a session
 * never do (invariant 16).
 *
 * Pure and free of the DOM, because the API runs it: the owner's phone stamps the parts the way
 * the parent's own phone would, and the server folds them into the parent's stored blob through
 * `mergeState` — the same rules a second phone's sync meets — and then keeps only those parts of
 * the result. Nothing else in the blob is touched, and its `updatedAt` is left alone: the parts
 * carry their own stamps, and a newer blob clock would hand the parent's older profile scalars a
 * win over edits their phone has not pushed yet.
 */

export type LinkedPart = "eating" | "water" | "dinner" | "training" | "tests";

export const LINKED_PARTS: readonly LinkedPart[] = ["eating", "water", "dinner", "training", "tests"];

/** One day's self-test counts, typed by an owner who timed them beside the parent (ADR-056). The
 *  day's `tests` block merges whole by its later `at`, so the block travels whole with its day. */
export interface TestsPart {
  ds: string;
  tests: DayTests;
}

export interface DinnerPart {
  dinnerRemindOn?: boolean;
  dinnerRemindAt?: string;
  dinnerRemindSetAt: number;
}

export interface LinkedWrite {
  eating?: EatingProfile;
  water?: WaterSettings;
  dinner?: DinnerPart;
  /** the exercise answers and the plan they set (ADR-050; the family screens arrive with ADR-056) */
  training?: TrainingProfile;
  /** one day's test counts (ADR-056) */
  tests?: TestsPart;
}

/** A stamp this far past the server's clock is refused: a phone set a day ahead would otherwise
 *  lock the parent out of their own answers until that day came. */
export const LINKED_SKEW_MS = 5 * 60 * 1000;

/** Far more than a full questionnaire or a bottle list; a cap so a link cannot fill a blob. */
export const LINKED_PART_MAX_BYTES = 16 * 1024;

/** The parts as they stand in `S`, to send after a writer has changed them. Only stamped parts
 *  are sent — an unstamped one was never set by anybody and has nothing to say. */
export function linkedParts(S: KayaState, parts: readonly LinkedPart[] = LINKED_PARTS): LinkedWrite {
  const p = S.profile;
  const out: LinkedWrite = {};
  if (parts.includes("eating") && p.eating?.at !== undefined) out.eating = p.eating;
  if (parts.includes("water") && p.water && p.water.at > 0) out.water = p.water;
  if (parts.includes("training") && p.training?.at !== undefined) out.training = p.training;
  if (parts.includes("tests")) {
    // the day the writer just stamped: the latest block of all
    let best: TestsPart | null = null;
    for (const [ds, L] of Object.entries(S.logs)) {
      const t = L?.tests;
      if (t && typeof t.at === "number" && (!best || t.at > best.tests.at)) best = { ds, tests: t };
    }
    if (best) out.tests = best;
  }
  if (parts.includes("dinner") && p.dinnerRemindSetAt !== undefined) {
    const d: DinnerPart = { dinnerRemindSetAt: p.dinnerRemindSetAt };
    if (p.dinnerRemindOn !== undefined) d.dinnerRemindOn = p.dinnerRemindOn;
    if (p.dinnerRemindAt !== undefined) d.dinnerRemindAt = p.dinnerRemindAt;
    out.dinner = d;
  }
  return out;
}

function isPlain(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === "object" && !Array.isArray(v);
}

function stampOk(v: unknown, now: number): v is number {
  return typeof v === "number" && Number.isFinite(v) && v > 0 && v <= now + LINKED_SKEW_MS;
}

/**
 * Checks a write from the network. Returns the reason it is refused, or null. The values inside
 * a part are the parent's own settings and the link trusts the reader with them, so only the
 * shape the merge relies on is checked: a plain object, a real stamp, a bounded size, and
 * nothing beyond the four parts.
 */
export function linkedWriteProblem(w: unknown, now: number): string | null {
  if (!isPlain(w)) return "Nothing to write";
  const keys = Object.keys(w);
  if (!keys.length) return "Nothing to write";
  const extra = keys.find((k) => !(LINKED_PARTS as readonly string[]).includes(k));
  if (extra) return `A linked account cannot write ${extra}`;
  for (const k of LINKED_PARTS) {
    const part = w[k];
    if (part === undefined) continue;
    if (!isPlain(part)) return `${k} must be an object`;
    if (JSON.stringify(part).length > LINKED_PART_MAX_BYTES) return `${k} is too large`;
    const stamp =
      k === "dinner" ? part.dinnerRemindSetAt : k === "tests" ? (isPlain(part.tests) ? part.tests.at : undefined) : part.at;
    if (!stampOk(stamp, now)) return `${k} needs a stamp from a phone whose clock is right`;
  }
  const d = w.dinner;
  if (isPlain(d)) {
    if (d.dinnerRemindOn !== undefined && typeof d.dinnerRemindOn !== "boolean") return "dinner.dinnerRemindOn must be true or false";
    if (d.dinnerRemindAt !== undefined && (typeof d.dinnerRemindAt !== "string" || !/^([01]\d|2[0-3]):[0-5]\d$/.test(d.dinnerRemindAt)))
      return "dinner.dinnerRemindAt must be HH:MM";
  }
  const t = w.tests;
  if (isPlain(t)) {
    const problem = testsProblem(t, now);
    if (problem) return problem;
  }
  return null;
}

const DS_RE = /^\d{4}-\d{2}-\d{2}$/;
const TEST_COUNTS = ["chairStand", "balanceSecs", "tug", "step"] as const;

/** The counts are typed on another phone: each a real, non-negative figure, the balance stage one
 *  of STEADI's five, the name a short one, and the day not one still to come. */
function testsProblem(t: Record<string, unknown>, now: number): string | null {
  if (typeof t.ds !== "string" || !DS_RE.test(t.ds)) return "tests.ds must be a day";
  if (t.ds > todayStr(new Date(now + LINKED_SKEW_MS))) return "tests.ds is a day still to come";
  const v = t.tests as Record<string, unknown>;
  for (const k of TEST_COUNTS) {
    const x = v[k];
    if (x !== undefined && (typeof x !== "number" || !Number.isFinite(x) || x < 0 || x > 1000)) return `tests.${k} must be a count`;
  }
  const st = v.balanceStage;
  if (st !== undefined && !(st === 0 || st === 1 || st === 2 || st === 3 || st === 4)) return "tests.balanceStage must be 0 to 4";
  if (v.by !== undefined && (typeof v.by !== "string" || v.by.length > 60)) return "tests.by must be a name";
  return null;
}

const DINNER_KEYS = ["dinnerRemindOn", "dinnerRemindAt", "dinnerRemindSetAt"] as const;

/** `p` with the parts of `from` named in `parts` — absent in `from` means absent in the result. */
function withParts(p: Profile, from: Partial<Profile>, parts: readonly LinkedPart[]): Profile {
  const out: Profile = { ...p };
  const put = <K extends keyof Profile>(k: K) => {
    if (from[k] === undefined) delete out[k];
    else out[k] = from[k] as Profile[K];
  };
  if (parts.includes("eating")) put("eating");
  if (parts.includes("water")) put("water");
  if (parts.includes("training")) put("training");
  if (parts.includes("dinner")) for (const k of DINNER_KEYS) put(k);
  return out;
}

/** `logs` with one day's `tests` block replaced — every other day, and every other field of that
 *  day, as it was. A day the parent never opened is started as the app's own writers start one. */
function withTests(logs: Record<string, DayLog>, ds: string, tests: DayTests | undefined): Record<string, DayLog> {
  const day: DayLog = { ...(logs[ds] ?? { done: {}, treadmill: null }) };
  if (tests) day.tests = tests;
  else delete day.tests;
  return { ...logs, [ds]: day };
}

export interface LinkedResult {
  state: KayaState;
  /** Parts the write named that the stored copy out-stamped: the parent changed them on their
   *  own phone after the reader last looked, and their change stands. */
  overruled: LinkedPart[];
}

/**
 * Folds a linked write into the parent's stored state. The write is merged exactly as a second
 * phone's copy would be, and only the parts it named are taken from the result.
 */
export function applyLinkedWrite(stored: Stamped, w: LinkedWrite, now = Date.now()): LinkedResult {
  const base = stored.state;
  const parts = LINKED_PARTS.filter((k) => w[k] !== undefined);
  const theirs: Partial<Profile> = {
    eating: w.eating,
    water: w.water,
    training: w.training,
    ...(w.dinner || {}),
  };
  const incoming: KayaState = { ...base, profile: withParts(base.profile, theirs, parts) };
  if (w.tests) incoming.logs = withTests(base.logs, w.tests.ds, w.tests.tests);
  const merged = mergeState(stored, { state: incoming, updatedAt: stored.updatedAt }, now);
  const state: KayaState = { ...base, profile: withParts(base.profile, merged.profile, parts) };
  if (w.tests) state.logs = withTests(base.logs, w.tests.ds, merged.logs[w.tests.ds]?.tests);

  const p = state.profile;
  const overruled = parts.filter((k) =>
    k === "eating"
      ? p.eating?.at !== w.eating?.at
      : k === "water"
        ? p.water?.at !== w.water?.at
        : k === "training"
          ? p.training?.at !== w.training?.at
          : k === "tests"
            ? state.logs[w.tests?.ds ?? ""]?.tests?.at !== w.tests?.tests.at
            : p.dinnerRemindSetAt !== w.dinner?.dinnerRemindSetAt
  );
  return { state, overruled };
}
